import { components } from "@/api-schema";
import { cn } from "@/common/helpers/utils";
import { PrivateAssetImage } from "@/components/private-asset-image/private-asset-image";

type Brand = Pick<components["schemas"]["BrandResponse"], "name" | "logoUrl">;

export enum BrandLogoVariant {
  DEFAULT = "DEFAULT",
  MEDIUM = "MEDIUM",
  COMPACT = "COMPACT",
}

export interface BrandLogoProps {
  brand: Brand;
  variant?: BrandLogoVariant;
  className?: string;
}

const getLogoVariantClasses = (variant: BrandLogoVariant): string => {
  switch (variant) {
    case BrandLogoVariant.COMPACT:
      return "w-6 max-h-6 text-xs font-normal";
    case BrandLogoVariant.MEDIUM:
      return "w-10 max-h-10 text-xs font-normal";
    case BrandLogoVariant.DEFAULT:
    default:
      return "w-12 max-h-12 text-lg font-semibold";
  }
};

const getFallbackVariantClasses = (variant: BrandLogoVariant): string => {
  switch (variant) {
    case BrandLogoVariant.COMPACT:
      return "size-6 text-xs font-normal";
    case BrandLogoVariant.MEDIUM:
      return "size-10 text-xs font-normal";
    case BrandLogoVariant.DEFAULT:
    default:
      return "size-12 text-lg font-semibold";
  }
};

export const BrandLogo = ({ brand, variant = BrandLogoVariant.DEFAULT, className }: BrandLogoProps) => {
  const logoUrl = brand.logoUrl;

  if (logoUrl) {
    return (
      <PrivateAssetImage
        src={logoUrl}
        alt={brand.name || "Brand logo"}
        // Logos are recoloured with filters instead of a CSS mask, because masks are fetched without the CloudFront cookies
        className={cn("h-auto object-contain brightness-0 dark:invert", getLogoVariantClasses(variant), className)}
      />
    );
  }

  return (
    <div
      className={cn(
        "bg-muted text-muted-foreground flex aspect-square items-center justify-center rounded-full border font-semibold uppercase",
        getFallbackVariantClasses(variant),
        className,
      )}
    >
      {brand.name?.trimStart().slice(0, 1) || "?"}
    </div>
  );
};
